// =====================================================================
// paginas/usuarios.js — Acesso, permissões, liberação e 2FA.
//
// O fluxo, herdado do Monteiro de Moraes e endurecido aqui:
//
//   1. a pessoa é cadastrada (ou se cadastra)
//   2. alguém completa o cadastro: FOTO e telefone
//   3. um master LIBERA o acesso — e só consegue se o cadastro estiver
//      completo (a trava é do banco, migration 015)
//   4. a própria pessoa ativa o 2FA na sua conta
//
// Por que a foto é obrigatória para liberar: quem entra aqui mexe em
// contrato, em cobrança e em dado pessoal de terceiros. O master que
// aprova precisa saber QUEM está aprovando, e um nome digitado não é
// ninguém.
//
// Quem ativa o 2FA é o DONO da conta — o segredo é gerado contra o token
// dele e o código sai do celular dele. Master "ativando o 2FA de outro"
// seria teatro. O que o master faz é ver quem ainda não ativou, e cobrar.
// =====================================================================

import { sb, api } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, tabela, badge, modal, cabecalho, checar } from '../ui.js';
import { data } from '../../../../packages/ui/format.js';
import { minhaConta, mostrarFoto } from './conta.js';

const ABAS = ['cadastro', 'imoveis', 'contratos', 'financeiro',
              'comunicacao', 'notificacoes', 'funcionarios', 'usuarios'];

const MASTER = (p) => ['admin', 'super_admin'].includes(p.papel);
const completo = (p) => !!(p.foto_url && String(p.foto_url).trim())
                     && !!(p.telefone && String(p.telefone).trim());

export async function pagUsuarios(el, estado) {
  // usuario_mfa NÃO é lida daqui: a tabela tem RLS sem policies, e o
  // navegador não a enxerga. O segredo TOTP não tem o que fazer no browser.
  // A RPC devolve só o booleano e a contagem de códigos — que é tudo que
  // esta tela precisa saber.
  const [perfis, mfa] = await Promise.all([
    sb.from('perfis').select('*').order('nome'),
    sb.rpc('mfa_ativos')
  ]);

  const lista = checar(perfis);
  const info2fa = new Map((mfa.data || []).map(m => [m.user_id, m]));
  const com2fa = new Set((mfa.data || []).filter(m => m.ativo).map(m => m.user_id));
  const eu = estado.perfil || {};
  const recarregar = () => pagUsuarios(el, estado);

  el.innerHTML = '';
  el.appendChild(cabecalho({
    titulo: 'Usuários',
    acao: 'Minha conta',
    onAcao: () => minhaConta(lista.find(p => p.id === eu.id) || eu, recarregar)
  }));

  // ── Avisos que exigem ação ────────────────────────────────────────
  const pendentes = lista.filter(p => !p.aprovado);
  if (pendentes.length) {
    const a = document.createElement('div');
    a.className = 'aviso atencao';
    a.innerHTML = `${icon('alerta')} <div><strong>${pendentes.length} usuário(s) aguardando liberação.</strong>
      Ninguém entra no sistema sem um master aprovar — e só se liberar quem tem cadastro completo.</div>`;
    el.appendChild(a);
  }

  const semMfa = lista.filter(p => MASTER(p) && !com2fa.has(p.id));
  if (semMfa.length) {
    const souEu = semMfa.some(m => m.id === eu.id);
    const a = document.createElement('div');
    a.className = 'aviso erro';
    a.innerHTML = `${icon('seguranca')} <div><strong>${semMfa.length} master sem 2FA:</strong>
      ${escapeHtml(semMfa.map(m => m.nome).join(', '))}.
      Master sem segundo fator é a porta mais larga do sistema — ele edita tudo.
      ${souEu ? '<button class="link" id="ativar-meu-2fa">Ativar o meu agora</button>' : ''}</div>`;
    el.appendChild(a);
    const b = a.querySelector('#ativar-meu-2fa');
    if (b) b.onclick = () => minhaConta(lista.find(p => p.id === eu.id) || eu, recarregar);
  }

  // ── Tabela ────────────────────────────────────────────────────────
  const div = document.createElement('div');
  div.innerHTML = tabela([
    { th: '', cls: 'col-foto', td: p => `<div class="foto-perfil pequena" data-foto="${escapeHtml(p.foto_url || '')}"
        data-id="${p.id}">${p.foto_url ? '' : icon('pessoa')}</div>` },

    { th: 'Nome', td: p => `<strong>${escapeHtml(p.nome)}</strong>
        ${p.id === eu.id ? badge('você', 'azul') : ''}` },

    { th: 'E-mail', td: p => escapeHtml(p.email) },

    { th: 'Papel', td: p => badge(p.papel, MASTER(p) ? 'azul' : 'cinza') },

    { th: 'Abas liberadas', td: p => {
        if (MASTER(p)) return badge('todas', 'azul');
        const on = ABAS.filter(a => p.permissoes?.[a] === true);
        return on.length ? escapeHtml(on.join(', ')) : badge('padrão do papel', 'cinza');
      } },

    { th: '2FA', td: p => {
        if (!com2fa.has(p.id)) {
          return badge(MASTER(p) ? 'obrigatório' : '—', MASTER(p) ? 'vermelho' : 'cinza');
        }
        const restantes = (info2fa.get(p.id) || {}).codigos_restantes;
        // Quantos códigos restam importa: descobrir que acabaram no dia em
        // que o celular quebrou é tarde demais.
        return badge('ativo', 'verde')
          + (restantes != null && restantes <= 2
              ? ' ' + badge(`${restantes} código(s)`, 'ambar')
              : '');
      } },

    { th: 'Situação', td: p => p.aprovado
        ? badge('liberado', 'verde')
        : completo(p)
          ? badge('pronto para liberar', 'ambar')
          : badge('cadastro incompleto', 'vermelho') },

    { th: 'Último acesso', td: p => p.ultimo_acesso ? data(p.ultimo_acesso) : '—' },

    { th: '', cls: 'acoes', td: p => `
        <button class="icone" data-editar="${p.id}" title="Editar cadastro">${icon('editar')}</button>
        <button class="icone" data-perm="${p.id}" title="Papel e permissões">${icon('chave')}</button>
        ${com2fa.has(p.id) && p.id !== eu.id
          ? `<button class="icone" data-reset="${p.id}" title="Resetar o 2FA (perdeu o celular)">${icon('seguranca')}</button>`
          : ''}
        ${p.aprovado
          ? `<button class="icone" data-bloq="${p.id}" title="Revogar acesso">${icon('cadeado')}</button>`
          : `<button class="link" data-lib="${p.id}">Liberar</button>`}` }
  ], lista, 'Nenhum usuário.');

  el.appendChild(div.firstElementChild);

  // Fotos: URL assinada, uma a uma (o bucket é privado, e continua sendo).
  el.querySelectorAll('[data-foto]').forEach(d => {
    if (d.dataset.foto) mostrarFoto(d, d.dataset.foto);
  });

  // ── Liberar ───────────────────────────────────────────────────────
  el.querySelectorAll('[data-lib]').forEach(b => b.onclick = async () => {
    const p = lista.find(x => x.id === b.dataset.lib);

    // A trava real é do banco. Esta checagem existe só para explicar ANTES,
    // em vez de deixar o Postgres devolver um erro cru na cara da pessoa.
    if (!completo(p)) {
      return formEditar(p, recarregar,
        'Cadastro incompleto: falta ' +
        [!p.foto_url && 'a foto', !p.telefone && 'o telefone'].filter(Boolean).join(' e ') +
        '. Complete para liberar o acesso.');
    }

    try {
      checar(await sb.from('perfis').update({ aprovado: true }).eq('id', p.id));
      recarregar();
    } catch (e) {
      alert(e.message);
    }
  });

  // ── Revogar ───────────────────────────────────────────────────────
  el.querySelectorAll('[data-bloq]').forEach(b => b.onclick = async () => {
    const p = lista.find(x => x.id === b.dataset.bloq);
    if (p.id === eu.id) return alert('Você não pode revogar o próprio acesso.');
    if (!confirm(`Revogar o acesso de ${p.nome}? Ele não consegue mais entrar.`)) return;
    checar(await sb.from('perfis').update({ aprovado: false }).eq('id', p.id));
    recarregar();
  });

  // ── Resetar o 2FA de alguém ───────────────────────────────────────
  //
  // O dia em que isso salva a operação: a pessoa perdeu o celular E o papel
  // com os códigos. Sem esta porta, ela está permanentemente fora — e se
  // for a única master, a imobiliária inteira está.
  //
  // Não é bypass: resetar APAGA o 2FA, e o alvo é obrigado a cadastrar um
  // novo no próximo login. Ninguém entra sem segundo fator por causa disso.
  el.querySelectorAll('[data-reset]').forEach(b => b.onclick = async () => {
    const p = lista.find(x => x.id === b.dataset.reset);
    if (!confirm(
      `Resetar o 2FA de ${p.nome}?\n\n`
      + 'O cadastro atual é apagado e ele(a) terá que configurar um novo no próximo login. '
      + 'Faça isto só se a pessoa perdeu o celular E os códigos de recuperação.\n\n'
      + 'A ação fica registrada na auditoria com o seu nome.')) return;

    try {
      const r = await api('mfa-reset', { user_id: p.id });
      alert(r.mensagem);
      recarregar();
    } catch (e) {
      alert(e.message);
    }
  });

  // ── Editar cadastro ───────────────────────────────────────────────
  el.querySelectorAll('[data-editar]').forEach(b => b.onclick = () => {
    const p = lista.find(x => x.id === b.dataset.editar);
    if (p.id === eu.id) return minhaConta(p, recarregar);   // a própria conta tem o 2FA
    formEditar(p, recarregar);
  });

  // ── Papel e permissões ────────────────────────────────────────────
  el.querySelectorAll('[data-perm]').forEach(b => b.onclick = () => {
    const p = lista.find(x => x.id === b.dataset.perm);
    modal({
      titulo: `Papel e permissões — ${p.nome}`,
      aviso: MASTER(p)
        ? 'Master vê tudo, independente do que for marcado aqui.'
        : 'Marque as abas que este usuário pode abrir. Desmarcado = a aba nem aparece para ele.',
      dados: { papel: p.papel, ...(p.permissoes || {}) },
      campos: [
        { nome: 'papel', label: 'Papel', tipo: 'select', full: true,
          opcoes: ['operador', 'admin', 'super_admin', 'proprietario', 'inquilino', 'prestador'] },
        ...ABAS.map(a => ({ nome: a, label: a[0].toUpperCase() + a.slice(1), tipo: 'checkbox' }))
      ],
      onSalvar: async (d) => {
        const { papel, ...perms } = d;
        checar(await sb.from('perfis').update({ papel, permissoes: perms }).eq('id', p.id));
        recarregar();
      }
    });
  });
}

// ─── Editar o cadastro de outra pessoa ───────────────────────────────
//
// O e-mail NÃO é editável: ele é a identidade no Auth, e trocar aqui
// deixaria o login apontando para um e-mail que não existe mais no perfil.
// Quem precisa mudar de e-mail vira um usuário novo — é mais honesto.
function formEditar(p, aoSalvar, aviso) {
  const fundo = document.createElement('div');
  fundo.className = 'modal-fundo';
  fundo.innerHTML = `
    <div class="modal">
      <header>
        <h3>Cadastro — ${escapeHtml(p.nome)}</h3>
        <button class="icone" data-fechar>${icon('erro')}</button>
      </header>
      <div class="corpo">
        ${aviso ? `<div class="aviso atencao">${icon('alerta')} ${escapeHtml(aviso)}</div>` : ''}

        <div class="conta-topo">
          <div class="foto-perfil" id="previa">${p.foto_url ? '' : icon('pessoa')}</div>
          <div>
            <label class="botao pequeno">
              ${icon('upload')} ${p.foto_url ? 'Trocar foto' : 'Enviar foto'}
              <input type="file" id="foto" accept="image/*" hidden>
            </label>
            <div class="dica" style="margin-top:6px">Obrigatória para liberar o acesso.</div>
          </div>
        </div>

        <label>Nome<input id="nome" value="${escapeHtml(p.nome || '')}" required></label>
        <label>Telefone<input id="telefone" value="${escapeHtml(p.telefone || '')}"
               placeholder="(31) 99999-9999"></label>
        <label>E-mail
          <input value="${escapeHtml(p.email)}" disabled>
          <small class="dica">O e-mail é a identidade do login e não muda aqui.</small>
        </label>

        <div id="msg"></div>
      </div>
      <footer>
        <button data-fechar>Cancelar</button>
        <button class="primaria" id="salvar">Salvar</button>
      </footer>
    </div>`;

  document.body.appendChild(fundo);
  const fechar = () => fundo.remove();
  fundo.querySelectorAll('[data-fechar]').forEach(b => (b.onclick = fechar));
  fundo.onclick = (e) => { if (e.target === fundo) fechar(); };

  const previa = fundo.querySelector('#previa');
  if (p.foto_url) mostrarFoto(previa, p.foto_url);

  let arquivo = null;
  fundo.querySelector('#foto').onchange = (e) => {
    arquivo = e.target.files[0];
    if (arquivo) previa.innerHTML = `<img src="${URL.createObjectURL(arquivo)}" alt="">`;
  };

  fundo.querySelector('#salvar').onclick = async () => {
    const btn = fundo.querySelector('#salvar');
    btn.disabled = true;
    btn.textContent = 'Salvando…';
    try {
      const patch = {
        nome: fundo.querySelector('#nome').value.trim(),
        telefone: fundo.querySelector('#telefone').value.trim() || null
      };
      if (!patch.nome) throw new Error('O nome é obrigatório.');

      if (arquivo) {
        const ext = (arquivo.name.split('.').pop() || 'jpg').toLowerCase();
        const caminho = `${p.tenant_id}/${p.id}.${ext}`;
        const { error } = await sb.storage.from('perfis')
          .upload(caminho, arquivo, { upsert: true, contentType: arquivo.type });
        if (error) throw new Error(error.message);
        patch.foto_url = caminho;
      }

      checar(await sb.from('perfis').update(patch).eq('id', p.id));
      fechar();
      aoSalvar();
    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Salvar';
      fundo.querySelector('#msg').innerHTML = `<div class="aviso erro">${escapeHtml(e.message)}</div>`;
    }
  };
}
